import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core'
import {
  DataContextBuilder,
  ElderFormFieldNoSpinnerDirective,
  ElderMaxValidator,
  ElderMinValidator,
  ElderTableComponent,
  IDataContext,
  Sort,
  SortUtil,
} from '@elderbyte/ngx-starter'
import { PackagingItem } from '../package'
import { BehaviorSubject, combineLatest, Observable } from 'rxjs'
import { PackagingMaterialSpec } from '../types/package-type'
import { map, switchMap } from 'rxjs/operators'
import { MaterialService } from '../../materials/material.service'
import { Material } from '../../materials/material'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { MaterialComponent } from '../../orders/work-items/bom/material-component'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { MatIcon } from '@angular/material/icon'
import { MatIconButton } from '@angular/material/button'
import { AsyncPipe } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { MatInput } from '@angular/material/input'
import { MatError, MatFormField } from '@angular/material/form-field'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { MatSort } from '@angular/material/sort'

export class PackagingTableRecord {
  constructor(
    public materialId: string,
    public quantity: number,
    public material?: Material
  ) {}
}

@Component({
  selector: 'app-packaging-material-table',
  templateUrl: './packaging-material-table.component.html',
  styleUrls: ['./packaging-material-table.component.scss'],
  imports: [
    ElderTableComponent,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatFormField,
    MatInput,
    FormsModule,
    ElderFormFieldNoSpinnerDirective,
    ElderMinValidator,
    ElderMaxValidator,
    MatError,
    MatIconButton,
    MatIcon,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class PackagingMaterialTableComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private _packagingItems$ = new BehaviorSubject<PackagingItem[]>([])
  private _packagingMaterialSpecs$ = new BehaviorSubject<PackagingMaterialSpec[]>([])

  public readonly data$ = new BehaviorSubject<IDataContext<PackagingTableRecord>>(null)

  public minQuantity = 0
  public maxQuantity = 999

  @Output()
  public readonly packagingItemUpdate = new EventEmitter<PackagingItem>()

  @Output()
  public readonly packagingItemsChange = new EventEmitter<PackagingItem[]>()

  @Input()
  public set packagingItems(items: PackagingItem[]) {
    this._packagingItems$.next(items)
  }

  @Input()
  public set packagingMaterialSpecs(specs: PackagingMaterialSpec[]) {
    this._packagingMaterialSpecs$.next(specs)
  }

  private materialService = inject(MaterialService)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    combineLatest([this._packagingItems$, this._packagingMaterialSpecs$])
      .pipe(switchMap(([items, specs]) => this.buildDataContext(items, specs)))
      .subscribe((dataContext) => {
        dataContext.start()
        this.data$.next(dataContext)
      })
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onIncrement(record: PackagingTableRecord): void {
    const newValue = record.quantity + 1
    if (newValue <= this.maxQuantity) {
      record.quantity = newValue
      this.onRecordChange(record)
    }
  }

  public onDecrement(record: PackagingTableRecord): void {
    const newValue = record.quantity - 1
    if (newValue >= this.minQuantity) {
      record.quantity = newValue
      this.onRecordChange(record)
    }
  }

  public onRecordChange(record: PackagingTableRecord): void {
    this.emitCurrentModel()
  }

  private emitCurrentModel(): void {
    const tableRecords = this.data$.getValue()
    const packagingItems = tableRecords.snapshot.data.map((record) =>
      this.createPackagingItem(record)
    )
    this.packagingItemsChange.emit(packagingItems)
  }

  private createPackagingItem(tableRecord: PackagingTableRecord): PackagingItem {
    const material = new MaterialComponent()
    material.materialId = tableRecord.materialId
    material.quantity = tableRecord.quantity
    return new PackagingItem(material)
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private buildDataContext(
    items: PackagingItem[],
    specs: PackagingMaterialSpec[]
  ): Observable<IDataContext<PackagingTableRecord>> {
    return this.buildTableData(items, specs).pipe(
      map((tableData) => this.buildDataContextInternal(tableData))
    )
  }

  private buildTableData(
    items: PackagingItem[],
    specs: PackagingMaterialSpec[]
  ): Observable<PackagingTableRecord[]> {
    const materialIds = [
      ...new Set([
        ...specs.map((spec) => spec.materialId),
        ...items.map((item) => item.material.materialId),
      ]),
    ]
    return this.loadMaterials(materialIds).pipe(
      map((materials) => this.populateTableData(items, specs, materials))
    )
  }

  private loadMaterials(ids: string[]): Observable<Material[]> {
    return this.materialService.findByIds(ids)
  }

  private populateTableData(
    items: PackagingItem[],
    specs: PackagingMaterialSpec[],
    materials: Material[]
  ): PackagingTableRecord[] {
    return materials.map((material) => {
      const itemWithQuantity = items.find((item) => item.material.materialId === material.id)
      const specWithPreset = specs.find(
        (spec) => spec.materialId === material.id && spec.presetQuantity != null
      )
      let quantity = 0
      if (itemWithQuantity != null) {
        quantity = itemWithQuantity.material.quantity
      } else if (specWithPreset != null) {
        quantity = specWithPreset.presetQuantity
      }

      return new PackagingTableRecord(material.id, quantity, material)
    })
  }

  private buildDataContextInternal(
    tableData: PackagingTableRecord[]
  ): IDataContext<PackagingTableRecord> {
    return DataContextBuilder.start<PackagingTableRecord>()
      .localSort((mat) => SortUtil.sortData(mat, [new Sort('materialId', 'asc')]))
      .buildLocal(tableData, 'materialId')
  }
}
